import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
} from '@mui/material';
import { Add, Edit, Delete, Inventory as InventoryIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import { InventoryService } from '../services/inventoryService';
import { Product } from '../types';

interface ProductFormData {
  name: string;
  category: string;
  cost: number;
  price: number;
  currentStock: number;
  minStockLevel: number;
  description?: string;
  isActive?: boolean;
}

export default function Inventory() {
  const { appUser } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const { control, handleSubmit, reset, formState: { errors } } = useForm<ProductFormData>({
    defaultValues: {
      category: 'wig',
      currentStock: 0,
      minStockLevel: 5,
    },
  });

  useEffect(() => {
    const loadProducts = async () => {
      if (!appUser?.salonId) return;

      try {
        const productsData = await InventoryService.getProducts(appUser.salonId);
        setProducts(productsData);
      } catch (error) {
        console.error('Error loading products:', error);
      }
    };

    loadProducts();
  }, [appUser]);

  const handleOpenDialog = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      reset({
        name: product.name,
        category: product.category,
        cost: product.cost,
        price: product.price,
        currentStock: product.currentStock,
        minStockLevel: product.minStockLevel,
        description: product.description,
      });
    } else {
      setEditingProduct(null);
      reset({
        category: 'wig',
        currentStock: 0,
        minStockLevel: 5,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProduct(null);
    reset();
  };

  const onSubmit = async (data: ProductFormData) => {
    if (!appUser?.salonId) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Filter out undefined and empty string values to prevent Firebase errors
      const cleanData: Partial<ProductFormData> = Object.fromEntries(
        Object.entries(data).filter(([_, value]) => value !== undefined && value !== '')
      ) as Partial<ProductFormData>;

      if (editingProduct) {
        // Update existing product
        await InventoryService.updateProduct(editingProduct.id, cleanData);
        setSuccess('Product updated successfully!');
      } else {
        // Add new product (ensure isActive is set to true for new products)
        // For new products, ensure all required fields are present
        const newProductData: Omit<Product, 'id' | 'salonId' | 'createdAt' | 'updatedAt'> = {
          name: data.name,
          category: data.category,
          cost: data.cost,
          price: data.price,
          currentStock: data.currentStock,
          minStockLevel: data.minStockLevel,
          isActive: true,
          ...(data.description && { description: data.description }),
        };
        await InventoryService.addProduct(appUser.salonId, newProductData);
        setSuccess('Product added successfully!');
      }

      // Refresh products list
      const updatedProducts = await InventoryService.getProducts(appUser.salonId);
      setProducts(updatedProducts);

      handleCloseDialog();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.message || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await InventoryService.deleteProduct(productId);
      setProducts(products.filter(p => p.id !== productId));
      setSuccess('Product deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.message || 'Failed to delete product');
    }
  };

  const handleAddStock = async (productId: string, quantity: number) => {
    const quantityStr = prompt('Enter quantity to add:');
    if (!quantityStr) return;

    const quantityToAdd = parseInt(quantityStr);
    if (isNaN(quantityToAdd) || quantityToAdd <= 0) return;

    try {
      await InventoryService.addStock(productId, quantityToAdd);
      // Refresh products list
      const updatedProducts = await InventoryService.getProducts(appUser!.salonId);
      setProducts(updatedProducts);
      setSuccess('Stock updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.message || 'Failed to update stock');
    }
  };

  const getStockStatus = (product: Product) => {
    if (product.currentStock === 0) return { label: 'Out of Stock', color: 'error' as const };
    if (product.currentStock <= product.minStockLevel) return { label: 'Low Stock', color: 'warning' as const };
    return { label: 'In Stock', color: 'success' as const };
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4" gutterBottom>
            Inventory Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track wig stock, manage suppliers, and monitor inventory levels.
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Product
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Quick Stats */}
      <Grid container spacing={3} mb={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Total Products
              </Typography>
              <Typography variant="h4" color="primary">
                {products.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Low Stock Items
              </Typography>
              <Typography variant="h4" color="warning.main">
                {products.filter(p => p.currentStock <= p.minStockLevel && p.isActive).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Total Value
              </Typography>
              <Typography variant="h4" color="secondary">
                ₦{products.reduce((sum, p) => sum + (p.cost * p.currentStock), 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Out of Stock
              </Typography>
              <Typography variant="h4" color="error.main">
                {products.filter(p => p.currentStock === 0 && p.isActive).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Products Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Products
          </Typography>

          {products.length === 0 ? (
            <Box textAlign="center" py={4}>
              <InventoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No products added yet
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Start by adding your first wig or accessory product.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
              >
                Add First Product
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Stock</TableCell>
                    <TableCell>Purchase Price</TableCell>
                    <TableCell>Selling Price</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.filter(p => p.isActive).map((product) => {
                    const stockStatus = getStockStatus(product);
                    return (
                      <TableRow key={product.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body1">{product.name}</Typography>
                            {product.description && (
                              <Typography variant="caption" color="text.secondary">
                                {product.description}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={product.category} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">{product.currentStock}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Min: {product.minStockLevel}
                          </Typography>
                        </TableCell>
                        <TableCell>₦{product.cost.toLocaleString()}</TableCell>
                        <TableCell>₦{product.price.toLocaleString()}</TableCell>
                        <TableCell>
                          <Chip
                            label={stockStatus.label}
                            color={stockStatus.color}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => handleAddStock(product.id, product.currentStock)}
                            color="primary"
                            size="small"
                            title="Add Stock"
                          >
                            <Add />
                          </IconButton>
                          <IconButton
                            onClick={() => handleOpenDialog(product)}
                            color="secondary"
                            size="small"
                            title="Edit"
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteProduct(product.id)}
                            color="error"
                            size="small"
                            title="Delete"
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Product Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Product name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Product Name"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="category"
                  control={control}
                  rules={{ required: 'Category is required' }}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Category</InputLabel>
                      <Select {...field} label="Category">
                        <MenuItem value="wig">Wig</MenuItem>
                        <MenuItem value="accessory">Accessory</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="cost"
                  control={control}
                  rules={{ required: 'Cost price is required', min: 0 }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Cost Price (₦)"
                      type="number"
                      error={!!errors.cost}
                      helperText={errors.cost?.message}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="price"
                  control={control}
                  rules={{ required: 'Selling price is required', min: 0 }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Selling Price (₦)"
                      type="number"
                      error={!!errors.price}
                      helperText={errors.price?.message}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="currentStock"
                  control={control}
                  rules={{ required: 'Current stock is required', min: 0 }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Current Stock"
                      type="number"
                      error={!!errors.currentStock}
                      helperText={errors.currentStock?.message}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="minStockLevel"
                  control={control}
                  rules={{ required: 'Minimum stock level is required', min: 0 }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Minimum Stock Level"
                      type="number"
                      error={!!errors.minStockLevel}
                      helperText={errors.minStockLevel?.message}
                    />
                  )}
                />
              </Grid>


              <Grid size={{ xs: 12 }}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Description (Optional)"
                      multiline
                      rows={2}
                      placeholder="Product description or notes"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Saving...' : (editingProduct ? 'Update Product' : 'Add Product')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
